import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Dialog from '@zenbusiness/zui/Dialog';
import DialogContent from '@zenbusiness/zui/DialogContent';
import IconButton from '@zenbusiness/zui/IconButton';
import PageLoading from '@zenbusiness/zui/PageLoading';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';
import { Navigate, Outlet } from 'react-router-dom';

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import CloseIcon from '@mui/icons-material/Close';

import { ZB_BANKING_SUNSET_PH3 } from '#client/constants/experimentsFlags';
import useBankingPath from '#client/hooks/useBankingPath';
import useFeatureFlag from '#client/hooks/useFeatureFlag';

import useBankingTransitionStepNavigation from './useBankingTransitionStepNavigation';

function BankingTransitionHeader({ onClose }) {
  const { goBack, hasPreviousStep, currentPageName } = useBankingTransitionStepNavigation();

  const trackBackClick = useButtonPressedCallback({
    elementText: 'Back',
    elementName: 'banking_transition_back',
    appPageName: currentPageName
  });

  const handleBack = () => {
    trackBackClick();
    goBack();
  };

  return (
    <Stack
      component="header"
      sx={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        px: { xs: 2, md: 4 },
        py: 0.5
      }}
    >
      <Box sx={{ minWidth: { xs: 40, md: 120 } }}>
        {hasPreviousStep && (
          <>
            <IconButton
              label="Back"
              onClick={handleBack}
              sx={{ display: { xs: 'flex', md: 'none' } }}
            >
              <ArrowBackIosIcon />
            </IconButton>
            <Button
              icon={<ArrowBackIosIcon />}
              onClick={handleBack}
              sx={{
                display: { xs: 'none', md: 'flex' },
                '& .ZuiButton-content': { marginInline: 0 }
              }}
              variant="text"
            >
              Back
            </Button>
          </>
        )}
      </Box>
      <Text
        color="primary"
        id="banking-transition-title"
        size="xl"
        weight="medium"
      >
        Banking options
      </Text>
      <Box sx={{ minWidth: { xs: 40, md: 120 }, display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton
          label="Close"
          onClick={onClose}
          sx={{ display: { xs: 'flex', md: 'none' } }}
        >
          <CloseIcon />
        </IconButton>
        <Button
          endIcon={<CloseIcon />}
          onClick={onClose}
          sx={{ display: { xs: 'none', md: 'flex' } }}
          variant="text"
        >
          Close
        </Button>
      </Box>
    </Stack>
  );
}

BankingTransitionHeader.propTypes = {
  onClose: PropTypes.func.isRequired
};

/**
 * Fullscreen shell for the banking transition flow. Redirects to the banking
 * dashboard while the Phase 3 flag is off and renders the routed step otherwise.
 * @returns {import('react').ReactElement} Loading state, redirect, or the flow shell
 */
export default function BankingTransitionLayout() {
  const bankingPath = useBankingPath();
  const { exitToDashboard, currentPageName } = useBankingTransitionStepNavigation();
  const trackCloseClick = useButtonPressedCallback({
    elementText: 'Close',
    elementName: 'banking_transition_close',
    appPageName: currentPageName
  });

  const handleClose = () => {
    trackCloseClick();
    exitToDashboard();
  };
  const [isTransitionEnabled, isLoadingFlag] = useFeatureFlag(ZB_BANKING_SUNSET_PH3);

  if (isLoadingFlag) {
    return <PageLoading />;
  }

  if (!isTransitionEnabled) {
    return (
      <Navigate
        replace
        to={`${bankingPath}/dashboard`}
      />
    );
  }

  return (
    <Dialog
      aria-labelledby="banking-transition-title"
      fullScreen
      onClose={handleClose}
      open
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: 'background.grey.main',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <BankingTransitionHeader onClose={handleClose} />
      <DialogContent sx={{ flex: 1, overflow: 'auto', px: { xs: 2, md: 4 }, py: 4 }}>
        <Outlet />
      </DialogContent>
    </Dialog>
  );
}
